import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { SonicUser, SonicUserData } from "../../common/classes/SonicUser";
import { RootState } from "../store";
import { getUsersAdmin } from "../../services/api/user/getUsersAdmin";
import { postCreateJikeiAccount } from "../../services/api/user/postCreateJikeiAccount";
import { getJikeiManagers } from "../../services/api/user/getJikeiManagers";
import { postUpdateUserChatDenialByAdmin } from "../../services/api/user/postUpdateUserChatDenialByAdmin";

interface AdminUserListState {
  total_entries: number;
  page: number;
  total_pages: number;
  users: Array<SonicUser>;
}

const initialState: AdminUserListState = {
  total_entries: 0,
  page: 0,
  total_pages: 0,
  users: [],
};

export const getUsers = createAsyncThunk(
  "admin/getUser",
  async (args: {
    auth_token: string;
    isSelect: boolean;
    params: {
      id?: string;
      page?: number;
      search?: string;
      per?: number;
      sort_by?: string;
      desc?: boolean;
      course?: string;
      accountType?: string;
    };
  }) => {
    const response = await getUsersAdmin(
      args.auth_token,
      args.isSelect,
      args.params
    );

    if (response !== null) {
      const users: Array<SonicUserData> = [];

      response.users.forEach((user) =>
        users.push(SonicUser.serializeUser(user))
      );

      return {
        total_pages: response.total_pages,
        page: response.page,
        total_entries: response.total_entries,
        users: users,
      };
    } else {
      return null;
    }
  }
);

export const getJikeiManager = createAsyncThunk(
  "admin/getJikeiManager",
  async (args: {
    auth_token: string;
    params: {
      id?: string;
      page?: number;
      per?: number;
      sort_by?: string;
      desc?: boolean;
    };
  }) => {
    const res = await getJikeiManagers(args.auth_token, args.params);

    if (res !== null) {
      const users: Array<SonicUserData> = [];

      res.users.forEach((user) => users.push(SonicUser.serializeUser(user)));

      return {
        total_pages: res.total_pages,
        page: res.page,
        total_entries: res.total_entries,
        users: users,
      };
    } else {
      return null;
    }
  }
);

export const addJikeiAccount = createAsyncThunk(
  "admin/addJikeiAccount",
  async (args: {
    auth_token: string;
    name: string;
    email: string;
    is_manager: boolean;
    school: string;
    course?: string;
  }) => {
    try {
      const res = await postCreateJikeiAccount(
        args.auth_token,
        args.name,
        args.email,
        args.is_manager,
        args.school,
        args.course
      );

      return res;
    } catch (e) {
      return e.response.data;
    }
  }
);

export const updateUserChatDenialByAdmin = createAsyncThunk(
  "admin/updateChatDenial",
  async (args: { auth_token: string; id: string; denial: boolean }) => {
    try {
      return await postUpdateUserChatDenialByAdmin(
        args.auth_token,
        args.id,
        args.denial
      );
    } catch (e) {
      return null;
    }
  }
);

export const adminDBSlice = createSlice({
  name: "adminUsers",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getUsers.pending, () => {});
    builder.addCase(getUsers.rejected, () => {});
    builder.addCase(getUsers.fulfilled, (state, action) => {
      if (action.payload !== null) {
        state.total_entries = action.payload.total_entries;
        state.page = action.payload.page;
        state.total_pages = action.payload.total_pages;
        state.users = action.payload.users;
      }
    });
    builder.addCase(getJikeiManager.pending, () => {});
    builder.addCase(getJikeiManager.rejected, () => {});
    builder
      .addCase(getJikeiManager.fulfilled, (state, action) => {
        if (action.payload !== null) {
          state.total_entries = action.payload.total_entries;
          state.page = action.payload.page;
          state.total_pages = action.payload.total_pages;
          state.users = action.payload.users;
        }
      })
      .addCase(updateUserChatDenialByAdmin.fulfilled, (state, action) => {
        if (action.payload !== null) {
          state.users[0].meta = {
            ...state.users[0].meta,
            chat_denial: action.payload,
          };
        }
      });
  },
});

export default adminDBSlice.reducer;

export const getAdminUsers = (state: RootState) => state.adminUsers.users;
export const getAdminUserTotalEntries = (state: RootState) =>
  state.adminUsers.total_entries;
export const getAdminUserCurrentPage = (state: RootState) =>
  state.adminUsers.page;
export const getAdminUserTotalPages = (state: RootState) =>
  state.adminUsers.total_pages;
